// A procedural, texture-free LENS FLARE drawn as a single additive fullscreen pass AFTER the composer:
// a big soft glare + a bright core + anamorphic streaks + ghost reflections strung along the star→center
// axis + a faint halo ring. Driven each frame by the star's screen position and an intensity that peaks
// when you look straight at it. This is what makes a star read as a brilliant LIGHT (mostly glare) rather
// than a small disc — most of "the star" you see is the flare, not the geometry.

import * as THREE from "three";

const FLARE_FRAG = /* glsl */ `
  precision highp float;
  varying vec2 vP;                 // clip-space [-1,1]
  uniform vec2 uStar;             // star position in NDC [-1,1]
  uniform float uIntensity;       // overall strength (0 = off)
  uniform vec3 uColor;            // star tint
  uniform float uAspect;          // width/height, to keep glows round

  vec2 ar(vec2 p){ return vec2(p.x * uAspect, p.y); }               // aspect-correct
  float glow(vec2 p, vec2 c, float r){ float d2 = dot(ar(p - c), ar(p - c)); return r * r / (r * r + d2); }

  void main(){
    if (uIntensity <= 0.001) discard;
    vec2 p = vP, s = uStar;
    vec3 warm = uColor;
    vec3 hot = mix(uColor, vec3(1.0), 0.7);                          // core burns toward white
    vec2 d = ar(p - s);
    float r = length(d);
    vec3 col = vec3(0.0);
    // GIANT layered glare — a huge soft halo down to a hot pinpoint core
    col += warm * glow(p, s, 0.85) * 0.18;
    col += warm * glow(p, s, 0.38) * 0.5;
    col += warm * glow(p, s, 0.12) * 0.9;
    col += hot  * glow(p, s, 0.03) * 1.8;
    col += hot  * glow(p, s, 0.008) * 3.2;
    // anamorphic streaks (long bright horizontal + a fainter vertical)
    col += hot * exp(-abs(d.y) * 165.0) * exp(-abs(d.x) * 0.8) * 1.5;
    col += hot * exp(-abs(d.x) * 230.0) * exp(-abs(d.y) * 1.8) * 0.5;
    // radial starburst rays fanning out of the core (kept fairly tight so HUD labels stay readable)
    float ang = atan(d.y, d.x);
    col += warm * pow(0.5 + 0.5 * cos(ang * 14.0), 6.0) * exp(-r * 4.6) * 0.42;
    // ghost reflections along the star→center axis (0,0) and past it
    for (int i = 1; i <= 6; i++){
      float t = 1.0 - 0.3 * float(i);
      col += mix(warm, hot, 0.4) * glow(p, s * t, 0.005 + 0.002 * float(i)) * 0.28;
    }
    // iris halo ring
    col += warm * exp(-abs(r - 0.22) * 20.0) * 0.14;
    gl_FragColor = vec4(col * uIntensity, 1.0);
  }
`;

export class LensFlare {
  private scene = new THREE.Scene();
  private cam = new THREE.Camera(); // pass-through (vertex shader writes clip space directly)
  private mat: THREE.ShaderMaterial;

  constructor() {
    this.mat = new THREE.ShaderMaterial({
      uniforms: {
        uStar: { value: new THREE.Vector2(0, 0) }, uIntensity: { value: 0 },
        uColor: { value: new THREE.Color(0xffffff) }, uAspect: { value: 1 },
      },
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending,
      vertexShader: "varying vec2 vP; void main(){ vP = position.xy; gl_Position = vec4(position.xy, 0.0, 1.0); }",
      fragmentShader: FLARE_FRAG,
    });
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.mat);
    quad.frustumCulled = false;
    this.scene.add(quad);
  }

  set(ndcX: number, ndcY: number, intensity: number, color: THREE.Color, aspect: number): void {
    const u = this.mat.uniforms;
    u.uStar.value.set(ndcX, ndcY);
    u.uIntensity.value = intensity;
    u.uColor.value.copy(color);
    u.uAspect.value = aspect;
  }

  /** Draw over the already-composited frame (additive). Call after composer.render(). */
  render(renderer: THREE.WebGLRenderer): void {
    if (this.mat.uniforms.uIntensity.value <= 0.001) return;
    const prev = renderer.autoClear;
    renderer.autoClear = false;
    renderer.render(this.scene, this.cam);
    renderer.autoClear = prev;
  }

  dispose(): void {
    this.mat.dispose();
    this.scene.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
  }
}

// ---- CLUSTER SUN FLARES ------------------------------------------------------------------------------
// The same lens-flare look as above, but SMALL and applied to MANY suns at once (the bright cluster stars
// in the galaxy/overview view). One fullscreen additive pass loops over up to N stars (glare + streaks +
// tight starburst per sun; no ghosts/iris — those are for the single hero flare). Drive each frame with the
// stars' projected NDC + per-sun intensity/color.
const CFLARE_N = 12;
const CFLARE_FRAG = /* glsl */ `
  precision highp float;
  varying vec2 vP;
  uniform vec2 uPos[${CFLARE_N}];
  uniform float uInt[${CFLARE_N}];
  uniform vec3 uCol[${CFLARE_N}];
  uniform int uN;
  uniform float uAspect;
  uniform float uScale;
  vec2 ar(vec2 p){ return vec2(p.x * uAspect, p.y); }
  void main(){
    vec3 col = vec3(0.0);
    for (int i = 0; i < ${CFLARE_N}; i++){
      if (i >= uN) break;
      float inten = uInt[i];
      if (inten <= 0.002) continue;
      vec2 d = ar(vP - uPos[i]);
      float dd = dot(d, d);
      float r = sqrt(dd);
      vec3 warm = uCol[i];
      vec3 hot = mix(warm, vec3(1.0), 0.7);
      float sc = uScale;
      vec3 c = warm * (sc * sc) / (sc * sc + dd) * 0.30;                    // soft halo
      float ri = sc * 0.30; c += hot * (ri * ri) / (ri * ri + dd) * 1.0;    // inner glow
      float rc = sc * 0.10; c += hot * (rc * rc) / (rc * rc + dd) * 2.2;    // hot core
      c += hot * exp(-abs(d.y) * 220.0) * exp(-abs(d.x) / (sc * 2.2)) * 0.8; // horizontal streak
      c += hot * exp(-abs(d.x) * 300.0) * exp(-abs(d.y) / (sc * 2.2)) * 0.35;// vertical streak
      float ang = atan(d.y, d.x);
      c += warm * pow(0.5 + 0.5 * cos(ang * 12.0), 7.0) * exp(-r / (sc * 1.1)) * 0.45; // tight starburst
      col += c * inten;
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class ClusterFlares {
  private scene = new THREE.Scene();
  private cam = new THREE.Camera();
  private mat: THREE.ShaderMaterial;
  readonly pos: THREE.Vector2[] = [];
  readonly col: THREE.Color[] = [];
  readonly inten: number[] = [];
  readonly max = CFLARE_N;

  constructor() {
    for (let i = 0; i < CFLARE_N; i++) { this.pos.push(new THREE.Vector2()); this.col.push(new THREE.Color()); this.inten.push(0); }
    this.mat = new THREE.ShaderMaterial({
      uniforms: {
        uPos: { value: this.pos }, uInt: { value: this.inten }, uCol: { value: this.col },
        uN: { value: 0 }, uAspect: { value: 1 }, uScale: { value: 0.08 },
      },
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending,
      vertexShader: "varying vec2 vP; void main(){ vP = position.xy; gl_Position = vec4(position.xy, 0.0, 1.0); }",
      fragmentShader: CFLARE_FRAG,
    });
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.mat);
    quad.frustumCulled = false;
    this.scene.add(quad);
  }

  /** After filling pos[]/col[]/inten[] for `count` suns, set the draw params. */
  set(count: number, aspect: number, scale: number): void {
    this.mat.uniforms.uN.value = count;
    this.mat.uniforms.uAspect.value = aspect;
    this.mat.uniforms.uScale.value = scale;
  }

  /** Draw over the composited frame (additive). Call after composer.render(). */
  render(renderer: THREE.WebGLRenderer): void {
    if (this.mat.uniforms.uN.value <= 0) return;
    const prev = renderer.autoClear;
    renderer.autoClear = false;
    renderer.render(this.scene, this.cam);
    renderer.autoClear = prev;
  }

  dispose(): void {
    this.mat.dispose();
    this.scene.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
  }
}
